import { captureException } from '@sentry/browser'
import type { Schema, z } from 'zod'

import { Config } from '../../../../config.ts'
import { handleJSONResponse } from '../utils/handleResponse.ts'
import { objectToFormData } from '../utils/objectToFormData.ts'

const config = Config()

export type Body = Record<string, string | Blob> | undefined
export interface FetcherOptions<I extends Schema = never, O extends Schema = never> {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: I extends Schema ? z.infer<I> : unknown
  token?: string
  signal?: AbortSignal
  in?: I
  out?: O
}

/**
 * Send a request to the PHP API.
 */
export async function fetcher<I extends Schema = never, O extends Schema = never>(
  path: string,
  { body, method = 'GET', signal, token, ...schema }: FetcherOptions<I, O> = {},
): Promise<O extends Schema ? z.infer<O> : unknown> {
  const url = config.webapiEndpoint + path

  try {
    if (schema.in) {
      const inResult = schema.in.parse(body)
      if (!inResult.success) {
        captureException(inResult.error, {
          extra: { url },
        })
      }
    }

    const json = await fetch(url, {
      method,
      mode: 'cors',
      credentials: 'include',
      body: body ? objectToFormData(body) : undefined,
      signal,
      headers: {
        Accept: 'application/json',
        ...(token ? { 'X-CSRF-Token': token } : {}),
      },
    })
      // Catchable errors! (only network errors, and CORS errors)
      // Must be before .then(), otherwise all validation errors will get
      // swallowed by the generic 'Smth went wrong'  message.
      .catch((cause) => {
        if (cause instanceof TypeError && cause.message === 'Failed to fetch') {
          throw new Error(
            `Network error while doing a ${method} request to ${url}. This usually means the server is unreachable or there's no internet connection.`,
            { cause },
          )
        }
        throw new Error(`Request error for ${method} ${url}: ${cause.message}`, { cause })
      })
      // Thenable errors! (includes all statuses returned from the server)
      .then(handleJSONResponse)

    if (schema.out) {
      const result = schema.out.safeParse(await json?.data)

      if (result.success) {
        return { ...json, data: result.data }
      }

      captureException(result.error, { extra: { url } })
    }

    return json
  } catch (error) {
    // (log and) capture the error
    console.error(error, url)
    captureException(error, { extra: { url } })
    // rethrow for SWR to handle
    throw error
  }
}
